
import React from 'react';
import { Star, Users, TrendingUp, Award, Smartphone } from 'lucide-react';
import { motion } from 'framer-motion';

interface StoreRating {
  playStore?: {
    rating: number;
    reviews: number;
    url: string;
  };
  appStore?: {
    rating: number;
    reviews: number;
    url: string;
  };
}

interface MobileHighlightProps {
  title: string;
  description: string;
  imageUrl: string;
  secondImageUrl: string;
  storeRatings: StoreRating;
  stats: {
    users?: number;
    downloads?: number;
    achievement?: string;
  };
  interestingFact: string;
}

const MobileHighlight: React.FC<MobileHighlightProps> = ({
  title,
  description,
  imageUrl,
  secondImageUrl,
  storeRatings,
  stats,
  interestingFact
}) => {
  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(142,227,205,0.1)_0%,rgba(255,255,255,0)_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(155,135,245,0.1)_0%,rgba(255,255,255,0)_60%)]" />
      
      {/* Floating elements */}
      <motion.div 
        className="absolute top-20 left-[15%] hidden lg:block"
        animate={{ 
          y: [0, -15, 0],
          rotate: [0, 5, 0]
        }}
        transition={{ 
          duration: 6, 
          repeat: Infinity,
          ease: "easeInOut" 
        }}
      >
        <div className="w-16 h-16 bg-gradient-to-br from-orcsys-100 to-orcsys-200 rounded-lg shadow-lg flex items-center justify-center">
          <Smartphone className="w-8 h-8 text-orcsys-500" />
        </div>
      </motion.div>
      
      <motion.div 
        className="absolute bottom-20 right-[15%] hidden lg:block"
        animate={{ 
          y: [0, 15, 0],
          rotate: [0, -5, 0]
        }}
        transition={{ 
          duration: 6, 
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
      >
        <div className="w-16 h-16 bg-gradient-to-br from-orcsys-100 to-orcsys-200 rounded-lg shadow-lg flex items-center justify-center">
          <Star className="w-8 h-8 text-orcsys-500" />
        </div>
      </motion.div>

      <div className="container px-4 mx-auto">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
              className="relative z-10"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">{title}</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">{description}</p>

              {/* Store ratings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {storeRatings.playStore && (
                  <motion.a 
                    whileHover={{ scale: 1.03, y: -5 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    href={storeRatings.playStore.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-white rounded-xl border border-slate-200 hover:border-orcsys-300 hover:shadow-lg transition-all"
                  >
                    <img 
                      src="/google-play-color.svg" 
                      alt="Google Play" 
                      className="w-10 h-10 mr-3"
                    />
                    <div>
                      <div className="flex items-center mb-1">
                        <Star className="w-4 h-4 text-yellow-400 mr-1 fill-yellow-400" />
                        <span className="font-semibold">{storeRatings.playStore.rating}</span>
                      </div>
                      <div className="text-sm text-slate-500">
                        {storeRatings.playStore.reviews.toLocaleString()} avis
                      </div>
                    </div>
                  </motion.a>
                )}

                {storeRatings.appStore && (
                  <motion.a 
                    whileHover={{ scale: 1.03, y: -5 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    href={storeRatings.appStore.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-4 bg-white rounded-xl border border-slate-200 hover:border-orcsys-300 hover:shadow-lg transition-all"
                  >
                    <img 
                      src="/app-store-color.svg" 
                      alt="App Store" 
                      className="w-10 h-10 mr-3"
                    />
                    <div>
                      <div className="flex items-center mb-1">
                        <Star className="w-4 h-4 text-yellow-400 mr-1 fill-yellow-400" />
                        <span className="font-semibold">{storeRatings.appStore.rating}</span>
                      </div>
                      <div className="text-sm text-slate-500">
                        {storeRatings.appStore.reviews.toLocaleString()} avis
                      </div>
                    </div>
                  </motion.a>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {stats.users && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <Users className="w-6 h-6 text-orcsys-500 mb-2" />
                    <div className="text-2xl font-bold text-slate-900 mb-1">
                      {stats.users.toLocaleString()}+
                    </div>
                    <div className="text-sm text-slate-600">Utilisateurs actifs</div>
                  </motion.div>
                )}
                {stats.downloads && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <TrendingUp className="w-6 h-6 text-orcsys-500 mb-2" />
                    <div className="text-2xl font-bold text-slate-900 mb-1">
                      {stats.downloads.toLocaleString()}+
                    </div>
                    <div className="text-sm text-slate-600">Téléchargements</div>
                  </motion.div>
                )}
                {stats.achievement && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <Award className="w-6 h-6 text-orcsys-500 mb-2" />
                    <div className="text-sm font-medium text-slate-900 mb-1 line-clamp-2">
                      {stats.achievement}
                    </div>
                    <div className="text-sm text-slate-600">Distinction</div>
                  </motion.div>
                )}
              </div>

              {/* Interesting fact */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-orcsys-50 to-white p-6 rounded-xl border border-orcsys-100 shadow-sm"
              >
                <div className="text-sm font-medium text-orcsys-600 mb-2">Le saviez-vous ?</div>
                <p className="text-slate-700">{interestingFact}</p>
              </motion.div>
            </motion.div>

            {/* Image */}
            <div
              className="relative"
            >
              <div className="relative flex items-center justify-center">
                {/* First Phone */}
                <div
                  className="relative z-20"
                >
                  <div className="relative border-[10px] border-slate-800 rounded-[40px] shadow-2xl overflow-hidden 
                    h-[550px] w-[260px] bg-white"
                  >
                    {/* Phone notch */}
                    <div className="absolute top-0 left-20 w-20 h-4 bg-slate-900 rounded-b-md"></div>
                    
                    <div className="h-full w-full overflow-hidden">
                      <img 
                        src={imageUrl} 
                        alt={title}
                        className="w-full h-full object-fill"
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-orcsys-500/10 via-transparent to-orcsys-500/10" />
                    </div>
                    
                  </div>
                  
                  {/* Phone reflection */}
                  <div className="absolute -bottom-10 left-5 right-5 h-20 bg-black/10 blur-xl rounded-full transform scale-x-75" />
                </div>

                {/* Second Phone */}
                <div
                  className="relative -ml-20 mt-20 z-10"
                >
                  <div className="relative border-[10px] border-slate-800 rounded-[40px] shadow-2xl overflow-hidden 
                    h-[480px] w-[230px] bg-white"
                  >
                    {/* Phone notch */}
                    <div className="absolute top-0 left-20 w-20 h-4 bg-slate-900 rounded-b-md"></div>
                    
                    <div className="h-full w-full overflow-hidden">
                      <img 
                        src={secondImageUrl} 
                        alt={`${title} - Alternative View`}
                        className="w-full h-full object-fill"
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-orcsys-500/10 via-transparent to-orcsys-500/10" />
                    </div>
                    
                  </div>
                  
                  {/* Phone reflection */}
                  <div className="absolute -bottom-10 left-5 right-5 h-20 bg-black/10 blur-xl rounded-full transform scale-x-75" />
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-orcsys-500/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-orcsys-500/10 rounded-full blur-2xl" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-orcsys-300/10 rounded-full blur-3xl -z-10" />
              
              {/* Light beams */}
              <div className="absolute top-0 left-1/4 w-1 h-40 bg-orcsys-300/30 blur-sm transform rotate-45 -z-10" />
              <div className="absolute bottom-0 right-1/4 w-1 h-40 bg-orcsys-300/30 blur-sm transform -rotate-45 -z-10" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileHighlight;
